import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  CULTURE_OUTCOME_WORDS,
  parseMic,
  SUSCEPTIBILITY_WORDS,
  type CultureOutcome,
  type SusceptibilityResult,
} from '@wodi/domain';
import type {
  CultureView,
  LabMicroItemView,
  LabMicroList,
  LabReportView,
  LabSampleWorkView,
  PreliminaryCultureReportView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  Notice,
  RadioGroup,
  Row,
  SearchPicker,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { issuePreliminaryReport, microListQuery, saveCulture } from '../api/lab-qc.ts';
import { labKeys } from '../api/lab.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './lab.module.css';

// The culture on a lab report (docs/plans/phase3-lab-qc-and-microbiology.md §1.2, §5): entry
// for the technologist, the read-only culture wherever a report is shown, and preliminary
// reports, always marked Preliminary. S, I and R are shown as entered with their words; no
// interpretation is added.

interface SusceptibilityDraft {
  antibioticId: string;
  antibioticName: string;
  result: SusceptibilityResult;
  mic: string;
  zone: string;
}

interface IsolateDraft {
  isolateNumber: number;
  organismId: string;
  organismName: string;
  quantity: string;
  comment: string;
  susceptibilities: SusceptibilityDraft[];
}

function draftOf(culture: CultureView | null): IsolateDraft[] {
  return (culture?.isolates ?? []).map((i) => ({
    isolateNumber: i.isolateNumber,
    organismId: i.organismId,
    organismName: i.organismName,
    quantity: i.quantity ?? '',
    comment: i.comment ?? '',
    susceptibilities: i.susceptibilities.map((s) => ({
      antibioticId: s.antibioticId,
      antibioticName: s.antibioticName,
      result: s.result,
      mic: s.mic ?? '',
      zone: s.zoneMm === null ? '' : String(s.zoneMm),
    })),
  }));
}

const OUTCOME_OPTIONS = (['pending', 'no_growth', 'growth'] as const).map((id) => ({
  id,
  label: CULTURE_OUTCOME_WORDS[id],
}));

const RESULT_OPTIONS = (['S', 'I', 'R'] as const).map((id) => ({
  id,
  label: `${id}, ${SUSCEPTIBILITY_WORDS[id]}`,
}));

/** A type-to-search picker over an organism or antibiotic list (DESIGN.md §7). */
function MicroPicker({
  list,
  label,
  exclude,
  onSelect,
}: {
  list: LabMicroList;
  label: string;
  exclude: readonly string[];
  onSelect: (item: LabMicroItemView) => void;
}) {
  const items = useQuery(microListQuery(list));
  const [text, setText] = useState('');
  const query = text.trim().toLowerCase();
  const found = (items.data?.items ?? []).filter(
    (i) => i.status === 'active' && !exclude.includes(i.id) && i.name.toLowerCase().includes(query),
  );
  return (
    <SearchPicker
      label={label}
      inputValue={text}
      onInputChange={setText}
      status={
        items.isError
          ? 'error'
          : items.isPending
            ? 'searching'
            : query.length < 2
              ? 'idle'
              : 'ready'
      }
      items={found.slice(0, 20)}
      getKey={(i) => i.id}
      getTextValue={(i) => i.name}
      renderItem={(i) => i.name}
      onSelect={(i) => {
        onSelect(i);
        setText('');
      }}
      emptyMessage={`Nothing in the ${list} list matches. The lab manager adds them in Settings, Lab.`}
      errorMessage={`The ${list} list couldn't be loaded. Try again.`}
      onRetry={() => void items.refetch()}
    />
  );
}

/** The culture entry form for a report in progress. */
export function CultureEntryForm({ report }: { report: LabReportView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const culture = report.culture;
  const [outcome, setOutcome] = useState<CultureOutcome>(culture?.outcome ?? 'pending');
  const [setUpAt, setSetUpAt] = useState<string | null>(culture?.setUpAt ?? null);
  const [setUpNote, setSetUpNote] = useState(culture?.setUpNote ?? '');
  const [critical, setCritical] = useState(culture?.critical ?? false);
  const [comment, setComment] = useState(culture?.comment ?? '');
  const [isolates, setIsolates] = useState<IsolateDraft[]>(() => draftOf(culture));
  const [tried, setTried] = useState(false);

  const micError = (mic: string) =>
    mic.trim() === '' || parseMic(mic).ok ? undefined : 'A number, with < or > if needed.';
  const zoneError = (zone: string) =>
    zone.trim() === '' || /^\d{1,3}$/.test(zone.trim()) ? undefined : 'Whole millimetres.';
  const invalid = isolates.some((i) =>
    i.susceptibilities.some(
      (s) => micError(s.mic) !== undefined || zoneError(s.zone) !== undefined,
    ),
  );

  const save = useMutation({
    mutationFn: () =>
      saveCulture(report.id, {
        expectedRevision: report.revision,
        setUpAt,
        setUpNote: setUpNote.trim() === '' ? null : setUpNote.trim(),
        outcome,
        critical,
        comment: comment.trim() === '' ? null : comment.trim(),
        isolates:
          outcome === 'no_growth'
            ? []
            : isolates.map((i) => ({
                isolateNumber: i.isolateNumber,
                organismId: i.organismId,
                quantity: i.quantity.trim() === '' ? null : i.quantity.trim(),
                comment: i.comment.trim() === '' ? null : i.comment.trim(),
                susceptibilities: i.susceptibilities.map((s) => ({
                  antibioticId: s.antibioticId,
                  result: s.result,
                  mic: s.mic.trim() === '' ? null : s.mic.trim(),
                  zoneMm: s.zone.trim() === '' ? null : Number(s.zone.trim()),
                })),
              })),
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Culture saved' });
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });

  const patchIsolate = (n: number, patch: Partial<IsolateDraft>) => {
    setIsolates((list) => list.map((i) => (i.isolateNumber === n ? { ...i, ...patch } : i)));
  };
  const patchSusceptibility = (
    n: number,
    antibioticId: string,
    patch: Partial<SusceptibilityDraft>,
  ) => {
    setIsolates((list) =>
      list.map((i) =>
        i.isolateNumber === n
          ? {
              ...i,
              susceptibilities: i.susceptibilities.map((s) =>
                s.antibioticId === antibioticId ? { ...s, ...patch } : s,
              ),
            }
          : i,
      ),
    );
  };
  const nextNumber = Math.max(0, ...isolates.map((i) => i.isolateNumber)) + 1;

  return (
    <form
      className={styles.section}
      aria-label={`${report.testName} culture`}
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (!invalid && online) save.mutate();
      }}
    >
      <h3 className={styles.entryHeading}>Culture</h3>
      {!online ? (
        <Notice tone="info">Offline. The culture can be saved when the connection is back.</Notice>
      ) : null}
      <div className={styles.actions}>
        <Checkbox
          isSelected={setUpAt !== null}
          onChange={(on) => {
            setSetUpAt(on ? (culture?.setUpAt ?? new Date().toISOString()) : null);
          }}
        >
          {setUpAt === null ? 'Culture set up' : `Culture set up ${formatInstant(setUpAt)}`}
        </Checkbox>
      </div>
      <TextField
        label="Set-up note (optional)"
        description="Media and conditions, as the lab writes them."
        value={setUpNote}
        onChange={setSetUpNote}
      />
      <RadioGroup
        label="Outcome"
        orientation="horizontal"
        value={outcome}
        onChange={(value) => {
          setOutcome(value as CultureOutcome);
        }}
        options={OUTCOME_OPTIONS}
      />
      {outcome === 'growth'
        ? isolates.map((isolate) => (
            <div key={isolate.isolateNumber} className={styles.stack}>
              <div className={styles.actions}>
                <strong>{`Isolate ${String(isolate.isolateNumber)}: ${isolate.organismName}`}</strong>
                <Button
                  dense
                  variant="quiet"
                  onPress={() => {
                    setIsolates((list) =>
                      list.filter((i) => i.isolateNumber !== isolate.isolateNumber),
                    );
                  }}
                >
                  Remove isolate
                </Button>
              </div>
              <TextField
                label="Quantity (optional)"
                value={isolate.quantity}
                onChange={(value) => {
                  patchIsolate(isolate.isolateNumber, { quantity: value });
                }}
              />
              {isolate.susceptibilities.map((s) => (
                <div key={s.antibioticId} className={styles.row}>
                  <RadioGroup
                    label={s.antibioticName}
                    orientation="horizontal"
                    value={s.result}
                    onChange={(value) => {
                      patchSusceptibility(isolate.isolateNumber, s.antibioticId, {
                        result: value as SusceptibilityResult,
                      });
                    }}
                    options={RESULT_OPTIONS}
                  />
                  <TextField
                    label="MIC (optional)"
                    value={s.mic}
                    onChange={(value) => {
                      patchSusceptibility(isolate.isolateNumber, s.antibioticId, { mic: value });
                    }}
                    {...(tried && micError(s.mic) !== undefined
                      ? { errorMessage: micError(s.mic) ?? '' }
                      : {})}
                  />
                  <TextField
                    label="Zone (optional)"
                    unit="mm"
                    value={s.zone}
                    onChange={(value) => {
                      patchSusceptibility(isolate.isolateNumber, s.antibioticId, { zone: value });
                    }}
                    {...(tried && zoneError(s.zone) !== undefined
                      ? { errorMessage: zoneError(s.zone) ?? '' }
                      : {})}
                  />
                  <Button
                    dense
                    variant="quiet"
                    aria-label={`Remove ${s.antibioticName}`}
                    onPress={() => {
                      patchIsolate(isolate.isolateNumber, {
                        susceptibilities: isolate.susceptibilities.filter(
                          (x) => x.antibioticId !== s.antibioticId,
                        ),
                      });
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <MicroPicker
                list="antibiotics"
                label={`Add an antibiotic for isolate ${String(isolate.isolateNumber)}`}
                exclude={isolate.susceptibilities.map((s) => s.antibioticId)}
                onSelect={(item) => {
                  patchIsolate(isolate.isolateNumber, {
                    susceptibilities: [
                      ...isolate.susceptibilities,
                      {
                        antibioticId: item.id,
                        antibioticName: item.name,
                        result: 'S',
                        mic: '',
                        zone: '',
                      },
                    ],
                  });
                }}
              />
            </div>
          ))
        : null}
      {outcome === 'growth' && isolates.length < 10 ? (
        <MicroPicker
          list="organisms"
          label="Add an organism"
          exclude={[]}
          onSelect={(item) => {
            setIsolates((list) => [
              ...list,
              {
                isolateNumber: nextNumber,
                organismId: item.id,
                organismName: item.name,
                quantity: '',
                comment: '',
                susceptibilities: [],
              },
            ]);
          }}
        />
      ) : null}
      <TextArea
        label="Culture comment (optional)"
        description="Printed on the report, for example mixed growth."
        value={comment}
        onChange={setComment}
        rows={2}
      />
      <Checkbox
        isSelected={critical}
        onChange={setCritical}
        description="For example a positive blood culture. The ordering clinician is notified when the report or a preliminary report is issued."
      >
        Mark critical
      </Checkbox>
      {save.isError ? (
        <Notice tone="error">
          {errorText(save.error, "The culture wasn't saved. Try again.")}
        </Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          type="submit"
          isPending={save.isPending}
          pendingLabel="Saving…"
          isDisabled={!online}
        >
          Save culture
        </Button>
        <span className={styles.meta}>
          Save the culture before issuing a preliminary report or submitting.
        </span>
      </div>
    </form>
  );
}

/** A culture as read: outcome, isolates and their sensitivities. */
export function CultureSummary({ culture, label }: { culture: CultureView; label: string }) {
  return (
    <div className={styles.stack}>
      <p>
        <strong>{CULTURE_OUTCOME_WORDS[culture.outcome]}</strong>
        {culture.critical ? (
          <span className={styles.critical}> Marked critical by the lab</span>
        ) : null}
        {culture.setUpAt === null ? null : (
          <span className={styles.meta}>{` Set up ${formatInstant(culture.setUpAt)}`}</span>
        )}
      </p>
      {culture.isolates.map((isolate) => (
        <div key={isolate.isolateNumber} className={styles.stack}>
          <p>
            {`Isolate ${String(isolate.isolateNumber)}: `}
            <strong>{isolate.organismName}</strong>
            {isolate.quantity === null ? '' : `, ${isolate.quantity}`}
          </p>
          {isolate.susceptibilities.length === 0 ? (
            <p className={styles.meta}>No sensitivities entered.</p>
          ) : (
            <TableScroll>
              <Table
                dense
                aria-label={`${label}, isolate ${String(isolate.isolateNumber)} sensitivities`}
              >
                <TableHeader>
                  <Column isRowHeader>Antibiotic</Column>
                  <Column>Result</Column>
                  <Column numeric>MIC</Column>
                  <Column numeric>Zone</Column>
                </TableHeader>
                <TableBody>
                  {isolate.susceptibilities.map((s) => (
                    <Row key={s.antibioticId}>
                      <Cell>{s.antibioticName}</Cell>
                      <Cell>
                        <span className={s.result === 'S' ? undefined : styles.strong}>
                          {`${s.result} ${SUSCEPTIBILITY_WORDS[s.result]}`}
                        </span>
                      </Cell>
                      <Cell numeric>{s.mic ?? ''}</Cell>
                      <Cell numeric>{s.zoneMm === null ? '' : `${String(s.zoneMm)} mm`}</Cell>
                    </Row>
                  ))}
                </TableBody>
              </Table>
            </TableScroll>
          )}
        </div>
      ))}
      {culture.comment === null ? null : <p>{culture.comment}</p>}
    </div>
  );
}

/** One preliminary report, marked as such. */
export function PreliminaryReport({
  report,
  testName,
}: {
  report: PreliminaryCultureReportView;
  testName: string;
}) {
  return (
    <div className={styles.stack}>
      <div className={styles.actions}>
        <Tag state="pending">Preliminary</Tag>
        <span className={styles.meta}>
          {`Preliminary report ${String(report.sequence)}, issued ${formatInstant(report.issuedAt)} by ${report.issuedBy.displayName}. Not the final report.`}
        </span>
      </div>
      <CultureSummary
        culture={report.culture}
        label={`${testName}, preliminary ${String(report.sequence)}`}
      />
    </div>
  );
}

/** Preliminary reports on a report being worked on, and issuing one (plan §1.2.4, Q9). */
export function PreliminaryReports({
  report,
  work,
}: {
  report: LabReportView;
  work: LabSampleWorkView;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const issue = useMutation({
    mutationFn: () => issuePreliminaryReport(report.id, report.revision),
    onSuccess: async () => {
      toasts.show({ title: 'Preliminary report issued' });
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });
  const working =
    report.status === 'in_progress' ||
    report.status === 'awaiting_validation' ||
    report.status === 'technically_validated';
  const enteredByMe = report.culture?.enteredBy?.id === session?.user.id;
  const mustDiffer = work.settings.validatorMustDiffer && enteredByMe;
  const canIssue =
    session !== null && can(session, 'lab.result.validate') && working && report.culture !== null;
  if (report.preliminaryReports.length === 0 && !canIssue) return null;
  const latest = report.preliminaryReports.at(-1);
  return (
    <div className={styles.stack}>
      <h3 className={styles.entryHeading}>Preliminary reports</h3>
      {latest === undefined ? (
        <p className={styles.meta}>No preliminary report issued.</p>
      ) : (
        <PreliminaryReport report={latest} testName={report.testName} />
      )}
      {report.preliminaryReports.length > 1 ? (
        <p
          className={styles.meta}
        >{`${String(report.preliminaryReports.length - 1)} earlier preliminary reports are kept.`}</p>
      ) : null}
      {canIssue ? (
        <>
          {mustDiffer ? (
            <Notice tone="info">
              You entered this culture. Another person must issue the preliminary report.
            </Notice>
          ) : null}
          {issue.isError ? (
            <Notice tone="error">
              {errorText(issue.error, "The preliminary report wasn't issued.")}
            </Notice>
          ) : null}
          <div className={styles.actions}>
            <Button
              isDisabled={!online || mustDiffer}
              isPending={issue.isPending}
              pendingLabel="Issuing…"
              onPress={() => {
                issue.mutate();
              }}
            >
              Issue preliminary report
            </Button>
            <span className={styles.meta}>
              The clinician sees it marked Preliminary. A culture marked critical notifies them.
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}
